import React, {
	useMemo,
	useState,
} from 'react';
import {
	Button,
	Col,
	Divider,
	Popover,
	Row,
	Space,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faMinus,
	faPlus,
} from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ExportOutlined } from '@ant-design/icons';
import {
	ContractCompletionTypes,
	DATE_AND_TIME,
	FeatureFlags,
	NumberFormatStyle,
	PnlItemTypes,
	VesselOwnershipTypes,
} from '@shared/utils/constants';
import { useBunkerModal } from '@client/utils/hooks/useBunkerModal';
import Card from '@client/components/Card/Card';
import Table from '@client/components/Table/Table';
import { formatDate } from '@client/utils/formatDate';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import useNumberFormatStyle from '@client/utils/hooks/useExportNotation';
import CsvExport from '@client/components/Export/CsvExport/CsvExport';
import { useVoyage } from '../VoyageProvider/VoyageProvider';
import getTcPnlItems from '../ProfitAndLossTab/helpers/getTcPnlItems';
import getPnlTableColumns, { shouldInvert } from '../ProfitAndLossTab/helpers/pnlTableColumns';
import PnlOverride from '../ProfitAndLossTab/PnlOverride';
import { buildExportPnlBaseSection, formatPercent, handleNotation } from '../../helpers/pnlCsvExport';
import styles from './TcPnl.module.css';

export type TablePnlEntry = {
	item: string;
	actual: number;
	estimated: number;
	invoiced: number;
	type: string;
	bold?: boolean;
	number?: boolean;
};

type Props = {
	columnsToHide?: ('actual' | 'estimated' | 'invoiced' | 'variance')[];
	spanOverride?: {
		xs?: number;
		sm?: number;
		xl?: number;
		xxl?: number;
	};
	slim?: boolean;
};

const TcPnl = ({
	columnsToHide = [],
	spanOverride = {},
	slim,
}: Props) => {
	const isAlba = useFeatureFlag(FeatureFlags.ALBA);
	const {
		open,
		modal,
	} = useBunkerModal();

	const {
		voyageDetails,
		tcPnl: pnl,
		fixtureCurrency,
		vendorExpenses,
	} = useVoyage();

	const euEtsExpense = vendorExpenses?.rows.find((expense) => expense.isEuEts);
	const isEuEtsIceClassDiscounted = euEtsExpense?.isIceClass === true && euEtsExpense.overrideAmount == null;

	const [activeTab, setActiveTab] = useState('pnl');
	const [expandedTotalDays, setExpandedTotalDays] = useState(false);
	const { formatStyle } = useNumberFormatStyle(NumberFormatStyle.UkUs);

	const { revenueItems, expenseItems, resultsItems } = useMemo(() => {
		if (pnl == null) {
			return {
				revenueItems: [],
				expenseItems: [],
				resultsItems: [],
			};
		}

		const showTcInRedeliveryBunkers = (
			voyageDetails.completionType === ContractCompletionTypes.TC_IN_DELIVERY
		);

		const items = getTcPnlItems(
			pnl,
			open,
			showTcInRedeliveryBunkers,
			expandedTotalDays,
			voyageDetails.vessel.ownershipType === VesselOwnershipTypes.TC_IN,
			isEuEtsIceClassDiscounted,
		);
		const hasTotalDaysBreakdown = pnl.tcOutDays != null || pnl.tcBallastingDays != null;

		return {
			...items,
			resultsItems: items.resultsItems.map((row) => (
				row.item === 'Total Days' && hasTotalDaysBreakdown ?
					{
						...row,
						onClick: () => setExpandedTotalDays((v) => !v),
						icon: expandedTotalDays ? faMinus : faPlus,
						actionTestId: 'expand-total-days',
					} :
					row
			)),
		};
	}, [
		pnl, 
		voyageDetails.completionType, 
		open, 
		expandedTotalDays, 
		voyageDetails.vessel.ownershipType,
		isEuEtsIceClassDiscounted,
	]);

	const columns = getPnlTableColumns(
		fixtureCurrency,
		columnsToHide,
		true,
		pnl?.expectedOrActual,
	);
		
	const buildExportPnlSection = (sectionTitle: string, rows: TablePnlEntry[]) => {
		if (rows.length === 0) {
			return [];
		}

		const baseSection = buildExportPnlBaseSection(
			sectionTitle,
			['Description', 'Estimated', 'Expected', 'Invoiced', 'Var (Exp - Est)', '%'],
		);

		const bodyRows = rows.map((row) => {
			// Oor inversion logic for correct signing in TC PNL export
			const invert = shouldInvert(row.item as PnlItemTypes, true);
			const estimated = invert ? row.estimated * -1 : row.estimated;
			const actual = invert ? row.actual * -1 : row.actual;
			const invoiced = invert ? row.invoiced * -1 : row.invoiced;

			const variance = row.actual - row.estimated;
			const variancePercentage = row.estimated === 0 ? 0 : (variance / row.estimated) * 100;

			return [
				row.item,
				handleNotation(estimated, formatStyle),
				handleNotation(actual, formatStyle),
				handleNotation(invoiced, formatStyle),
				handleNotation(variance, formatStyle),
				formatPercent(variancePercentage, formatStyle),
			];
		});

		return [
			...baseSection,
			...bodyRows,
			[],
		];
	};

	const csvData = useMemo(() => {
		const titleRow = [`ClearVoyage TC PNL export - ${formatDate(new Date(), DATE_AND_TIME)}`];
		const currencyRow = [`Contract Currency: ${fixtureCurrency}`];

		return [
			titleRow,
			currencyRow,
			[],
			...buildExportPnlSection('Revenue', revenueItems),
			...buildExportPnlSection('Expenses', expenseItems),
			...buildExportPnlSection('Results', resultsItems),
		];
	}, [revenueItems, expenseItems, resultsItems]);

	let content: React.ReactNode;

	if (activeTab === 'alba') {
		content = (<PnlOverride voyageId={voyageDetails.id} />);
	} else {
		content = (
			<>
				<Divider>
					<Space>
						<FontAwesomeIcon icon={faPlus as IconProp} />
						Revenue
					</Space>
				</Divider>
				<Table<TablePnlEntry>
					size="small"
					pagination={false}
					columns={columns}
					dataSource={revenueItems}
					className={styles.table}
					bordered
				/>
				<Divider>
					<Space>
						<FontAwesomeIcon icon={faMinus as IconProp} />
						Expenses
					</Space>
				</Divider>
				<Table<TablePnlEntry>
					data-testid="expensesTable"
					size="small"
					pagination={false}
					columns={columns}
					dataSource={expenseItems}
					className={styles.table}
					bordered
				/>
				<Divider>Results</Divider>
				<Table<TablePnlEntry>
					size="small"
					pagination={false}
					columns={columns}
					dataSource={resultsItems}
					className={styles.table}
					bordered
				/>
			</>
		);
	}

	return (
		<>
			{modal}
			<Row align="middle" justify="center">
				<Col
					xs={spanOverride.xs ?? 24}
					sm={spanOverride.sm ?? 24}
					xl={spanOverride.xl ?? 20}
					xxl={spanOverride.xxl ?? 12}
				>
					<Card
						slim={slim}
						bodyStyle={{ paddingTop: 0 }}
						title="P&L Sheet"
						extra={activeTab === 'pnl' && (
							<Popover
								trigger="click"
								content={(
									<CsvExport
										data={csvData}
										filename={`ClearVoyage TC PNL export ${formatDate(new Date(), DATE_AND_TIME)}.csv`}
									/>
								)}
							>
								<Button icon={<ExportOutlined />}>
									Export
								</Button>
							</Popover>
						)}
						tabList={[
							{ label: 'P&L', key: 'pnl' },
							...(isAlba ? [{ label: 'P&L Overrides', key: 'alba' }] : []),
						]}
						onTabChange={(key) => setActiveTab(key)}
					>
						{content}
					</Card>
				</Col>
			</Row>
		</>
	);
};

export default TcPnl;
